import { useCallback, useState } from "react";
import { usePubmedQueryValidation } from "../../../../../hooks/usePubmedQueryValidation";
import {
  startPubmedIngest,
  type BatchJob,
  type PubmedQueryPreview,
} from "../../../../../lib/corpusApi";
import { PUBMED_INGEST_MAX_RESULTS } from "@shared/types/corpus";
import type { BatchJobParamsForm } from "../../batch_job_kind_contract";

const DEFAULT_MAX_RESULTS = 200;

export interface UsePubmedIngestParamsOptions {
  /** Whether the extraction service that answers a validation is up. Until it is, there is nothing
   *  to validate against, so Validate — and with it the job — stays unavailable. */
  extractionWarm: boolean;
}

export interface UsePubmedIngestParamsResult extends BatchJobParamsForm {
  /** Whether the extraction service is up; see UsePubmedIngestParamsOptions. */
  extractionWarm: boolean;
  query: string;
  setQuery: (query: string) => void;
  /** The limit as typed — kept as text so a half-typed number is not rewritten under the cursor. */
  maxResultsText: string;
  setMaxResultsText: (text: string) => void;
  /** Why the typed limit is unusable, or null. */
  maxResultsProblem: string | null;
  /** A structural mistake in the typed query that NCBI would not report itself, or null. */
  syntaxProblem: string | null;
  /** Whether Validate can be pressed: the extraction service is up, the query has no structural
   *  mistake, and no check is running. */
  canValidate: boolean;
  validate: () => void;
  validating: boolean;
  /** NCBI's refusal of the query, or the check's failure. Null once the query has been edited. */
  validationError: string | null;
  /** What PMC said about the query as it now stands; null until it is validated, and again once
   *  the query is edited. */
  preview: PubmedQueryPreview | null;
  /** How many articles the job would ingest: the limit, or every match when there are fewer. */
  articlesToIngest: number | null;
}

/** Reads the limit a person typed; null when it is not a whole number in range. */
function parseMaxResults(text: string): number | null {
  const value = Number(text);
  if (text.trim() === "" || !Number.isInteger(value)) return null;
  return value >= 1 && value <= PUBMED_INGEST_MAX_RESULTS ? value : null;
}

/**
 * The params a pubmed_ingest job is started with: a PMC query and a limit. The query must be
 * validated before the job can start, so a job is only ever started from a query whose count was
 * just seen.
 */
export function usePubmedIngestParams({
  extractionWarm,
}: UsePubmedIngestParamsOptions): UsePubmedIngestParamsResult {
  const [maxResultsText, setMaxResultsText] = useState(String(DEFAULT_MAX_RESULTS));
  const validation = usePubmedQueryValidation({ extractionWarm });
  const { trimmedQuery, preview } = validation;

  const maxResults = parseMaxResults(maxResultsText);
  const maxResultsProblem =
    maxResults === null
      ? `Enter a whole number from 1 to ${PUBMED_INGEST_MAX_RESULTS.toLocaleString()}.`
      : null;
  const canStart = preview !== null && preview.count > 0 && maxResults !== null;

  const start = useCallback(
    (name?: string): Promise<BatchJob> =>
      startPubmedIngest({ query: trimmedQuery, maxResults: maxResults ?? 0, name }),
    [trimmedQuery, maxResults]
  );

  return {
    extractionWarm,
    query: validation.query,
    setQuery: validation.setQuery,
    maxResultsText,
    setMaxResultsText,
    maxResultsProblem,
    syntaxProblem: validation.syntaxProblem,
    canValidate: validation.canValidate,
    validate: validation.validate,
    validating: validation.validating,
    validationError: validation.error,
    preview,
    articlesToIngest: preview && maxResults !== null ? Math.min(preview.count, maxResults) : null,
    canStart,
    start,
  };
}
